import { Link } from "react-router-dom";
import { ArrowUpRight, Rss } from "lucide-react";
import SEO from "../components/site/SEO";
import SectionHeader from "../components/site/SectionHeader";

const POSTS = [
  {
    tag: "Threat Intel",
    title: "Anatomy of a Modern Supply Chain Attack",
    excerpt:
      "How attackers chain trusted vendors into entry vectors — and what L2 SOC controls actually catch them.",
  },
  {
    tag: "VAPT",
    title: "Why Annual Pen Tests Aren't Enough Anymore",
    excerpt:
      "Continuous validation models are replacing the once-a-year ritual. Here's what that means for your CISO scorecard.",
  },
  {
    tag: "Compliance",
    title: "Building a Continuous SOC 2 Evidence Pipeline",
    excerpt:
      "From audit panic to evergreen evidence — the operational pattern Quasect deploys for SaaS clients.",
  },
  {
    tag: "Detection",
    title: "Detection Engineering 101: From Logs to Alerts",
    excerpt:
      "What separates a noisy SIEM from a sharp one — and the playbook our analysts run on day one.",
  },
  {
    tag: "Incident Response",
    title: "First 15 Minutes of an Active Incident",
    excerpt:
      "The exact runbook our L2 SOC executes when an alert turns critical. No fluff, just the steps.",
  },
  {
    tag: "AI",
    title: "Where AI Actually Helps in a SOC (and Where It Doesn't)",
    excerpt:
      "An honest take on AI-driven threat intel — what's coming, what's overhyped, and what we're shipping next.",
  },
];

export default function Blog() {
  return (
    <div data-testid="blog-page" className="bg-[#050507] text-white">
      <SEO
        title="Quasect Insights — Cybersecurity Field Notes & SOC Research"
        description="Field notes from the Quasect SOC: threat intelligence, VAPT, detection engineering, and compliance research."
        path="/blog"
      />

      <section className="relative overflow-hidden noise border-b border-white/10">
        <div className="absolute inset-0 bg-grid bg-grid-fade opacity-50" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 pt-24 pb-16 md:pt-32 md:pb-20">
          <div className="font-mono text-xs tracking-[0.3em] uppercase text-[#39FF14] flex items-center gap-3">
            <Rss className="w-3.5 h-3.5" /> INSIGHTS / NEWSROOM
          </div>
          <h1 className="font-display font-black mt-6 text-4xl sm:text-5xl md:text-6xl lg:text-[72px] tracking-tighter leading-[1.02] max-w-5xl break-words">
            Field notes from <span className="text-[#39FF14]">the SOC.</span>
          </h1>
          <p className="mt-6 text-lg md:text-xl text-[#8B93A5] max-w-3xl leading-relaxed">
            Threat intelligence, VAPT, detection engineering, and compliance research — written by
            the analysts who run them.
          </p>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <SectionHeader overline="Latest" title="Coming soon: weekly research drops." />
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {POSTS.map((p, i) => (
              <article
                key={i}
                data-testid={`blog-post-${i}`}
                className="tracing-card group bg-[#0A0A0D] border border-white/10 hover:border-[#00F0FF]/50 p-7 md:p-8 transition-all noise relative flex flex-col"
              >
                <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#00F0FF]">
                  {p.tag}
                </div>
                <h3 className="font-display font-bold text-xl md:text-2xl mt-4 leading-tight tracking-tight">
                  {p.title}
                </h3>
                <p className="mt-4 text-[#8B93A5] leading-relaxed flex-1">{p.excerpt}</p>
                <div className="mt-7 pt-5 border-t border-white/10 flex items-center justify-between font-mono text-[11px] uppercase tracking-wider text-[#8B93A5]">
                  <span>Coming Soon</span>
                  <span className="group-hover:text-[#39FF14] transition-colors flex items-center gap-1">
                    READ <ArrowUpRight className="w-3 h-3" />
                  </span>
                </div>
              </article>
            ))}
          </div>

          <div className="mt-16 border border-white/10 bg-[#0A0A0D] p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center gap-5 justify-between noise relative">
            <div>
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#00F0FF]">
                Subscribe
              </div>
              <h3 className="font-display font-bold text-2xl md:text-3xl mt-2">
                Get the Quasect briefing in your inbox.
              </h3>
            </div>
            <Link
              to="/contact"
              data-testid="blog-cta"
              className="bg-[#39FF14] text-black font-bold uppercase tracking-wider px-6 py-3.5 text-sm hover:bg-[#32E612] transition-colors flex items-center gap-2"
            >
              Request access <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
